import {
  Alert,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Icons,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from '@healthinal/ui';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { getImportFileErrorMessage, useUploadContactTracingFile } from '../../../api/created/importFile.ts';
import { GetPatientsBasicsType, PatientBasicsDto } from '../../../api/generated/model';
import { FileInput } from '../../../components/form/FileInput.tsx';
import { FormActionButtons } from '../../../components/form/FormActionButtons.tsx';
import { getContactTracingImportMessage } from '../../../utils/contactTracingImportCode.ts';
import { SelectPatient } from '../../components/SelectPatient.tsx';
import { ShowCountryCodesDialog } from './ShowCountyCodesDialog.tsx';

interface FormValues {
  file: File | null;
  patient: PatientBasicsDto | null;
}

export function ImportTracingContactsDialog({
  preselectedPatient,
  open,
  onClose,
}: {
  preselectedPatient: PatientBasicsDto | null;
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();

  const {
    watch,
    control,
    reset,
    handleSubmit,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: {
      file: null,
      patient: preselectedPatient,
    },
  });

  const fileValue = watch('file');
  const [isCountriesDialogOpen, setIsCountriesDialogOpen] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(null);

  const uploadContactTracingFileMutation = useUploadContactTracingFile();

  // defaultValues are only read on the first render, when usually no patient is selected yet, so the
  // patient selected on the page is set explicitly.
  useEffect(() => {
    if (open) {
      reset({ file: null, patient: preselectedPatient });
      setUploadErrorMessage(null);
    }
  }, [open, preselectedPatient]);

  useEffect(() => {
    setUploadErrorMessage(null);
  }, [fileValue]);

  const handleOnClose = () => {
    reset({ file: null, patient: preselectedPatient });
    setUploadErrorMessage(null);
    onClose();
  };

  const onSubmit = async ({ file, patient: selectedPatient }: FormValues) => {
    // The patient shown in the dialog: the one selected on the page, otherwise the one picked here.
    const patient = preselectedPatient ?? selectedPatient;
    setUploadErrorMessage(null);
    try {
      const response = await uploadContactTracingFileMutation.mutateAsync({ caseId: patient!.id, file: file! });
      if (!response.success || !response.fileId) {
        setUploadErrorMessage(getContactTracingImportMessage(response.code));
        return;
      }
      handleOnClose();
      void navigate({
        to: '/review/$fileId',
        params: { fileId: response.fileId },
        search: { patientId: patient!.id },
      });
    } catch (error) {
      setUploadErrorMessage(getImportFileErrorMessage(error));
    }
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 1200 }}>
        <ModalClose />
        <Typography level={'h4'}>Kontaktpersonen hinzufügen</Typography>
        <Divider />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={2}>
            <Stack direction={'row'} gap={2}>
              <FormControl sx={{ flex: 1 }} error={!!errors.patient}>
                <FormLabel>
                  <Typography>Kontakte von *</Typography>
                </FormLabel>
                {preselectedPatient ? (
                  <Typography>
                    {preselectedPatient.caseNumber ?? `${preselectedPatient.lastName} ${preselectedPatient.firstName}`}
                  </Typography>
                ) : (
                  <Controller
                    name={'patient'}
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <SelectPatient
                        onChange={onChange}
                        value={value}
                        patientsType={GetPatientsBasicsType.CONTACT_TRACING}
                        activeOnly
                        showCaseNumber
                      />
                    )}
                  />
                )}
                {errors.patient && <FormHelperText>Indexpatient muss angegeben werden</FormHelperText>}
              </FormControl>

              <FormControl sx={{ flex: 1 }} error={!!errors.file}>
                <FormLabel>
                  <Typography>Datei *</Typography>
                </FormLabel>
                <Controller
                  name={'file'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <FileInput
                      value={value}
                      onChange={onChange}
                      accept={'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
                      placeholder={'Datei hochladen'}
                    />
                  )}
                />
                {errors.file && <FormHelperText>Datei muss ausgewählt werden</FormHelperText>}
                <Button variant={'soft'} component={'a'} target={'_blank'} href={'contacts_import.xlsx'} sx={{ my: 1 }}>
                  Beispiel-Datei herunterladen
                </Button>
                <Button variant={'soft'} onClick={() => setIsCountriesDialogOpen(true)}>
                  ISO-Ländercodes anzeigen
                </Button>
              </FormControl>
            </Stack>

            {uploadErrorMessage && (
              <Alert color={'danger'} startDecorator={<Icons.ErrorOutline />}>
                {uploadErrorMessage}
              </Alert>
            )}

            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={uploadContactTracingFileMutation.isPending}
            />
          </Stack>
        </form>
        <ShowCountryCodesDialog open={isCountriesDialogOpen} onClose={() => setIsCountriesDialogOpen(false)} />
      </ModalDialog>
    </Modal>
  );
}
